import { IconCopy } from '@developerz/ui';
import { createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import { copyText } from '../lib/clipboard';
import { Button } from './ui/Button';

// A captioned block of text with a copy button — the shape a panel that hands
// over several artifacts needs, where <DonateBadgeSnippet/> and
// <MintedTokenNotice/> each hand over exactly one and carry their own copy.
//
// THE CAPTION IS THE BUTTON'S NAME. Three "Copy" buttons stacked in one panel
// are three identical accessible names, so a screen-reader user tabbing through
// hears the same word for the endpoint, the CLI line and the JSON. The caption
// is already on screen naming each one; `aria-label` reuses it rather than
// inventing a second wording that could drift from the visible text.
//
// Goes through `copyText` (`lib/clipboard.ts`), so a write that did not land
// never reports "Copied" — the refusal-vs-nothing-happened line.

export type CopySnippetProps = {
  /** Names what the block holds; rendered above it and used as the button's name. */
  readonly label: string;
  readonly snippet: string;
  /** Layout modifier for the host surface; the block itself is context-free. */
  readonly class?: string;
};

export function CopySnippet(props: CopySnippetProps): JSX.Element {
  const [copied, setCopied] = createSignal(false);
  const copy = async (): Promise<void> => {
    setCopied(await copyText(props.snippet));
  };

  return (
    <div class={props.class ? `copy-snippet ${props.class}` : 'copy-snippet'}>
      <p class="copy-snippet__label">{props.label}</p>
      <pre class="code-block">{props.snippet}</pre>
      <Button
        variant="outline"
        size="sm"
        aria-label={t('copy_snippet.aria', { label: props.label })}
        onClick={() => void copy()}
      >
        <IconCopy size="0.75rem" />
        {t(copied() ? 'copy_snippet.copied' : 'copy_snippet.copy')}
      </Button>
    </div>
  );
}
